/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { styled, useTheme } from '@mui/material/styles';
import SurfaceSection from "../Common/SurfaceSection";
import CircularProgress from '@mui/material/CircularProgress';
import { QuarterlyUniqueClient } from "../../types/Reports";
import useReportsService from "../../services/useReportsService";
import LineChart from "../Common/Charts/LineChart";
import { formatMedianNumber, formatNumber, formatPercentage } from "../../utils/formatNumber";

const CloseButton = styled(IconButton)(({ theme }) => `
    color: ${theme.palette.primary[100]};
    position: absolute;
    right: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
`);

function QuarterlyUniqueClientsReportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [quarterlyData, setQuarterlyData] = useState<QuarterlyUniqueClient[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const lineChartParentRef = useRef<HTMLDivElement>(null);

    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation("common");
    const { fetchQuarterlyUniqueClientsReportData } = useReportsService();

    const colorPalette = [
        theme.palette.primary.main,
        theme.palette.primary.dark,
    ];

    useEffect(() => {
        if (!open) return;

        setIsLoading(true);
        fetchQuarterlyUniqueClientsReportData().then((data) => {
            setQuarterlyData(data);
            setError(null);
        }).catch(() => {
            setError(t("reports.clientTypes.error-fetching-data"));
        }).finally(() => {
            setIsLoading(false);
        });
    }, [open, fetchQuarterlyUniqueClientsReportData, t]);

    const noData = useMemo(() => {
        return !quarterlyData || quarterlyData.length === 0;
    }, [quarterlyData]);

    const renderContent = () => {
        if (error) {
            return (
                <Box display="flex" justifyContent="center" my={2}>
                    <Typography variant="h6" align="center" width="100%" color="error">
                        {error}
                    </Typography>
                </Box>
            );
        }

        if (isLoading) {
            return (
                <Box display="flex" justifyContent="center" my={2}>
                    <CircularProgress />
                </Box>
            );
        }

        if (noData) {
            return (
                <Box display="flex" justifyContent="center" my={2}>
                    <Typography variant="h6" align="center" width="100%">
                        {t("reports.clientTypes.no-data")}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box>
                <LineChart
                    parentRef={lineChartParentRef}
                    colorPalette={colorPalette}
                    topBottomPadding={16}
                    leftRightPadding={16}
                    data={{
                        xAxis: {
                            data: quarterlyData.map((item) => item.quarterName),
                        },
                        series: [
                            {
                                id: 'uniqueCustomers',
                                label: t("reports.quarterlyUniqueClientsEvolution.uniqueClients"),
                                data: quarterlyData.map((item) => item.uniqueCustomers),
                                showMark: true,
                                area: true,
                            },
                            {
                                id: 'totalEntries',
                                label: t('reports.quarterlyUniqueClientsEvolution.totalEntries'),
                                data: quarterlyData.map((item) => item.totalEntries),
                                showMark: true,
                                area: true,
                            },
                        ],
                        legendItems: [
                            {
                                color: theme.palette.primary.main,
                                label: t("reports.quarterlyUniqueClientsEvolution.uniqueClients"),
                            },
                            {
                                color: theme.palette.primary.dark,
                                label: t('reports.quarterlyUniqueClientsEvolution.totalEntries'),
                            },
                        ],
                    }}
                    chartInitialSize={{ width: 800, height: 400 }}
                />
            </Box>
        )
    };

    const renderTable = () => {
        if (isLoading || noData) return null;

        return (
            <Box>
                <Table size="small" sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <TableHead>
                        <TableRow>
                            <TableCell align="left" sx={{ fontWeight: "bold" }}>{t("reports.quarterlyUniqueClientsEvolution.quarter")}</TableCell>
                            <TableCell align="left" sx={{ fontWeight: "bold" }}>{t("reports.quarterlyUniqueClientsEvolution.uniqueClients")}</TableCell>
                            <TableCell align="left" sx={{ fontWeight: "bold" }}>{t("reports.quarterlyUniqueClientsEvolution.totalEntries")}</TableCell>
                            <TableCell align="left" sx={{ fontWeight: "bold" }}>{t("reports.quarterlyUniqueClientsEvolution.mediumWashNoPerMonth")}</TableCell>
                            <TableCell align="left" sx={{ fontWeight: "bold" }}>{t("reports.quarterlyUniqueClientsEvolution.QoQUniqueClients")}</TableCell>
                            <TableCell align="left" sx={{ fontWeight: "bold" }}>{t("reports.quarterlyUniqueClientsEvolution.nrOfTotalQoQEntries")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {quarterlyData.map((row) => (
                            <TableRow key={row.quarterName}>
                                <TableCell>
                                    <span>{row.quarterName}</span>
                                </TableCell>
                                <TableCell align="center">{formatNumber(row.uniqueCustomers)}</TableCell>
                                <TableCell align="center">{formatNumber(row.totalEntries)}</TableCell>
                                <TableCell align="center">{formatMedianNumber(row.avgMonthlyEntries)}</TableCell>
                                <TableCell align="center">
                                    {!!row.uniqueCustomersQoQChange
                                        ? formatPercentage(row.uniqueCustomersQoQChange, true)
                                        : '-'
                                    }
                                </TableCell>
                                <TableCell align="center">
                                    {!!row.totalEntriesQoQChange
                                        ? formatPercentage(row.totalEntriesQoQChange, true)
                                        : '-'
                                    }
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        )
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <DialogTitle>
                {t("reports.quarterlyUniqueClientsEvolution.title")}
                <CloseButton aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </CloseButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}>
                <Box sx={{ mb: 2 }}>
                    <Typography>
                        {t("reports.quarterlyUniqueClientsEvolution.subtitle")}
                    </Typography>
                </Box>
                <Box
                    display="flex"
                    flex={1}
                    gap={2}
                    flexDirection={isTabletOrMobile ? "column" : "row"}
                >
                    {!isTabletOrMobile && (
                        <Box
                            flex={3}
                            display="flex"
                            flexDirection="column"
                            justifyContent="flex-end"
                        >
                            {renderTable()}
                        </Box>
                    )}

                    <SurfaceSection
                        ref={lineChartParentRef}
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            flex: isTabletOrMobile ? 12 : 9,
                            flexDirection: isTabletOrMobile ? "column" : "row",
                            justifyContent: "center",
                            minWidth: 0,
                            overflow: "hidden",
                        }}
                    >
                        {renderContent()}
                    </SurfaceSection>
                    {isTabletOrMobile && renderTable()}
                </Box>
            </DialogContent >
        </Dialog >
    );
};

export default QuarterlyUniqueClientsReportDialog;
